import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { KeyRound, CheckCircle, AlertCircle, Eye, EyeOff, ArrowLeft, Check } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';

export function ResetPasswordPage() {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const { updatePassword, session, isRecoverySession, loading: authLoading } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    // Check if we have a valid recovery session (user clicked reset link)
    // If not, redirect to login
    if (!authLoading) {
      if (!session || !isRecoverySession) {
        // No recovery session - redirect to login
        navigate('/auth', { replace: true });
      }
    }
  }, [session, isRecoverySession, authLoading, navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    // Validation
    if (password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setLoading(true);

    try {
      const { error } = await updatePassword(password);
      
      if (error) {
        setError(error.message);
        setLoading(false);
      } else {
        setSuccess(true);
        // Redirect to home after 2 seconds (App switches to Dashboard when authenticated)
        setTimeout(() => {
          navigate('/');
        }, 2000);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to update password');
      setLoading(false);
    }
  };

  // Show loading while checking session
  if (authLoading) {
    return (
      <div className="min-h-screen bg-alabaster flex items-center justify-center p-4">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-olive border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-sand font-light">Loading...</p>
        </div>
      </div>
    );
  }

  // If no recovery session, don't render the form (redirect will happen)
  if (!session || !isRecoverySession) {
    return null;
  }

  // Success state
  if (success) {
    return (
      <div className="min-h-screen bg-alabaster flex items-center justify-center p-4">
        {/* Background decoration */}
        <div className="absolute top-0 right-0 w-1/2 h-1/2 bg-gradient-to-bl from-olive/5 to-transparent pointer-events-none" />
        <div className="absolute bottom-0 left-0 w-1/2 h-1/2 bg-gradient-to-tr from-terracotta/5 to-transparent pointer-events-none" />

        <div className="w-full max-w-md relative z-10">
          <div className="bg-white border border-stone shadow-soft-xl rounded-4xl p-8 text-center space-y-6">
            <div className="flex justify-center">
              <div className="bg-olive/10 p-5 rounded-full border border-olive/20">
                <CheckCircle className="w-16 h-16 text-olive" />
              </div>
            </div>
            
            <h2 className="text-3xl font-serif font-light tracking-tight text-charcoal">
              Password Updated!
            </h2>
            
            <p className="text-sand font-light">
              Your password has been successfully reset.
            </p>

            <div className="bg-olive/10 border border-olive/20 rounded-2xl p-4">
              <p className="text-sm text-olive font-light">
                Redirecting you to your dashboard...
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-alabaster">
      {/* Background decoration */}
      <div className="absolute top-0 right-0 w-1/2 h-1/2 bg-gradient-to-bl from-olive/5 to-transparent pointer-events-none" />
      <div className="absolute bottom-0 left-0 w-1/2 h-1/2 bg-gradient-to-tr from-terracotta/5 to-transparent pointer-events-none" />

      <div className="container mx-auto px-4 py-8 relative z-10">
        {/* Logo */}
        <div className="flex items-center justify-center mb-12">
          <Link to="/" className="flex items-center gap-3 group">
            <img 
              src="/logo.webp" 
              alt="VirtualStagePro" 
              className="w-10 h-10 rounded-xl shadow-soft transition-transform duration-500 group-hover:-translate-y-0.5"
            />
            <span className="text-xl font-serif font-medium tracking-tight text-charcoal">
              VirtualStagePro
            </span>
          </Link>
        </div>

        <div className="flex items-center justify-center">
          <div className="w-full max-w-md">
            <div className="bg-white border border-stone shadow-soft-xl rounded-4xl overflow-hidden">
              {/* Header */}
              <div className="bg-gradient-to-br from-olive to-charcoal p-8">
                <div className="flex justify-center mb-4">
                  <div className="bg-alabaster/20 backdrop-blur-sm p-4 rounded-full">
                    <KeyRound className="w-12 h-12 text-alabaster" />
                  </div>
                </div>
                <h2 className="text-2xl font-serif font-light text-alabaster text-center tracking-tight">
                  Create New Password
                </h2>
                <p className="text-alabaster/80 text-center mt-2 font-light">
                  Enter your new password below
                </p>
              </div>

              {/* Form */}
              <div className="p-8">
                <form onSubmit={handleSubmit} className="space-y-6">
                  {error && (
                    <div className="bg-terracotta/10 border border-terracotta/30 rounded-2xl p-3 flex items-center gap-2">
                      <AlertCircle className="w-5 h-5 text-terracotta flex-shrink-0" />
                      <p className="text-sm text-terracotta font-light">{error}</p>
                    </div>
                  )}

                  <div>
                    <label htmlFor="password" className="block text-xs font-mono uppercase tracking-widest text-sand mb-3">
                      New Password
                    </label>
                    <div className="relative">
                      <input
                        id="password"
                        type={showPassword ? 'text' : 'password'}
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        required
                        minLength={6}
                        className="w-full px-5 py-3.5 pr-12 bg-alabaster border border-stone rounded-full text-charcoal placeholder:text-antique focus:outline-none focus:border-olive focus:ring-2 focus:ring-olive/10 transition-all duration-300 font-light"
                        placeholder="Enter new password"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-4 top-1/2 -translate-y-1/2 text-sand hover:text-charcoal transition-colors"
                      >
                        {showPassword ? (
                          <EyeOff className="w-5 h-5" />
                        ) : (
                          <Eye className="w-5 h-5" />
                        )}
                      </button>
                    </div>
                  </div>

                  <div>
                    <label htmlFor="confirmPassword" className="block text-xs font-mono uppercase tracking-widest text-sand mb-3">
                      Confirm New Password
                    </label>
                    <div className="relative">
                      <input
                        id="confirmPassword"
                        type={showConfirmPassword ? 'text' : 'password'}
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        required
                        minLength={6}
                        className="w-full px-5 py-3.5 pr-12 bg-alabaster border border-stone rounded-full text-charcoal placeholder:text-antique focus:outline-none focus:border-olive focus:ring-2 focus:ring-olive/10 transition-all duration-300 font-light"
                        placeholder="Confirm new password"
                      />
                      <button
                        type="button"
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        className="absolute right-4 top-1/2 -translate-y-1/2 text-sand hover:text-charcoal transition-colors"
                      >
                        {showConfirmPassword ? (
                          <EyeOff className="w-5 h-5" />
                        ) : (
                          <Eye className="w-5 h-5" />
                        )}
                      </button>
                    </div>
                  </div>

                  <div className="bg-alabaster border border-stone rounded-3xl p-4">
                    <p className="text-xs font-mono uppercase tracking-widest text-sand mb-3">Password Requirements</p>
                    <ul className="text-sm space-y-2">
                      <li className={`flex items-center gap-2 ${password.length >= 6 ? 'text-olive' : 'text-sand'} font-light`}>
                        <Check className={`w-4 h-4 ${password.length >= 6 ? 'opacity-100' : 'opacity-30'}`} />
                        At least 6 characters
                      </li>
                      <li className={`flex items-center gap-2 ${password === confirmPassword && password ? 'text-olive' : 'text-sand'} font-light`}>
                        <Check className={`w-4 h-4 ${password === confirmPassword && password ? 'opacity-100' : 'opacity-30'}`} />
                        Passwords match
                      </li>
                    </ul>
                  </div>

                  <button
                    type="submit"
                    disabled={loading || !password || !confirmPassword}
                    className="w-full bg-olive hover:bg-charcoal text-alabaster py-4 rounded-full font-medium transition-all duration-500 disabled:opacity-50 disabled:cursor-not-allowed shadow-soft hover:shadow-lift hover:-translate-y-0.5 flex items-center justify-center gap-2"
                  >
                    {loading ? (
                      <>
                        <div className="w-5 h-5 border-2 border-alabaster border-t-transparent rounded-full animate-spin" />
                        <span>Updating Password...</span>
                      </>
                    ) : (
                      <>
                        <KeyRound className="w-5 h-5" />
                        <span>Update Password</span>
                      </>
                    )}
                  </button>
                </form>

                {/* Back to home link */}
                <div className="mt-8 text-center">
                  <Link
                    to="/"
                    className="inline-flex items-center gap-2 text-sand hover:text-terracotta transition-colors duration-300 font-light text-sm"
                  >
                    <ArrowLeft className="w-4 h-4" />
                    Back to Home
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
